<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    max-width="550"
    persistent
    transition="dialog-bottom-transition"
  >
    <v-card class="whatsapp-card glass-panel border-opacity-10 rounded-xl overflow-hidden elevation-0 shadow-premium">
      <!-- Premium Green Header -->
      <div
        class="pa-8 text-center text-white"
        style="background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);"
      >
        <v-avatar size="72" color="white" class="mb-4 shadow-premium">
          <v-icon color="#25D366" size="44">mdi-whatsapp</v-icon>
        </v-avatar>
        <h2 class="text-h4 font-weight-black italic mb-1 letter-spacing-1">Secure Media Sync</h2>
        <p class="text-subtitle-2 font-weight-bold opacity-80 letter-spacing-1">
          WhatsApp Clinical Connectivity Protocol
        </p>

        <v-btn
          icon="mdi-close"
          variant="tonal"
          color="white"
          class="close-btn rounded-xl"
          @click="$emit('update:modelValue', false)"
          :disabled="isLoading"
        ></v-btn>
      </div>

      <v-card-text class="pa-10 bg-white-opacity-5">
        <!-- Professional Progress Indicator -->
        <div class="d-flex justify-center align-center mb-10">
          <div
            :class="[
              'step-indicator',
              { active: currentStep >= 1, complete: currentStep > 1 },
            ]"
          >
            <v-icon v-if="currentStep > 1" size="18">mdi-check</v-icon>
            <span v-else>1</span>
          </div>
          <div class="step-connector" :class="{ active: currentStep > 1 }"></div>
          <div
            :class="[
              'step-indicator',
              { active: currentStep >= 2, complete: currentStep > 2 },
            ]"
          >
            <v-icon v-if="currentStep > 2" size="18">mdi-check</v-icon>
            <span v-else>2</span>
          </div>
        </div>

        <!-- Step 1: Input & Method Selection -->
        <div v-if="currentStep === 1" class="step-content animate-slide-in-up">
          <p class="text-subtitle-1 font-weight-bold text-center text-grey-darken-3 mb-8 opacity-70">
            Authorize your professional WhatsApp number to initiate encrypted patient communications.
          </p>

          <v-form
            ref="phoneForm"
            v-model="isPhoneFormValid"
            @submit.prevent="requestOTP"
          >
            <div class="text-overline font-weight-black text-grey-darken-1 mb-2 letter-spacing-1">Authorized Number</div>
            <v-text-field
              v-model="whatsappNumber"
              placeholder="+234 800 000 0000"
              variant="outlined"
              prepend-inner-icon="mdi-phone-outline"
              :rules="phoneRules"
              class="rounded-xl premium-search-field mb-8"
              bg-color="white"
              hide-details="auto"
              :disabled="isLoading"
            ></v-text-field>

            <div class="text-overline font-weight-black text-grey-darken-1 mb-4 letter-spacing-1">Verification Architecture</div>
            <v-radio-group v-model="selectedMethod" class="method-selector-custom">
              <v-card
                :class="[
                  'method-premium-card mb-4 border-thin shadow-sm',
                  { selected: selectedMethod === 'whatsapp' },
                ]"
                @click="selectedMethod = 'whatsapp'"
                variant="flat"
              >
                <div class="d-flex align-center pa-5">
                  <v-radio value="whatsapp" color="#25D366" hide-details></v-radio>
                  <v-avatar size="44" color="rgba(37, 211, 102, 0.1)" class="mx-4 rounded-xl">
                    <v-icon color="#25D366">mdi-whatsapp</v-icon>
                  </v-avatar>
                  <div class="flex-grow-1">
                    <div class="font-weight-black text-grey-darken-4">WhatsApp Direct Payload</div>
                    <div class="text-caption font-weight-bold grey-darken-1 opacity-70">
                      Receive secure token via WhatsApp
                    </div>
                  </div>
                </div>
              </v-card>

              <v-card
                :class="[
                  'method-premium-card border-thin shadow-sm',
                  { selected: selectedMethod === 'email' },
                ]"
                @click="selectedMethod = 'email'"
                variant="flat"
              >
                <div class="d-flex align-center pa-5">
                  <v-radio value="email" color="primary" hide-details></v-radio>
                  <v-avatar size="44" color="rgba(var(--v-theme-primary), 0.1)" class="mx-4 rounded-xl">
                    <v-icon color="primary">mdi-email-lock-outline</v-icon>
                  </v-avatar>
                  <div class="flex-grow-1">
                    <div class="font-weight-black text-grey-darken-4">Secure Email Relay</div>
                    <div class="text-caption font-weight-bold grey-darken-1 opacity-70">
                      Forward token to {{ maskedEmail }}
                    </div>
                  </div>
                </div>
              </v-card>
            </v-radio-group>

            <v-btn
              type="submit"
              color="#25D366"
              size="x-large"
              block
              class="rounded-xl mt-10 text-white font-weight-black shadow-premium"
              :loading="isLoading"
              :disabled="!isPhoneFormValid"
            >
              Generate Verification Token
            </v-btn>
          </v-form>
        </div>

        <!-- Step 2: OTP Verification -->
        <div v-else-if="currentStep === 2" class="step-content text-center animate-slide-in-up">
          <v-avatar size="100" color="rgba(37, 211, 102, 0.1)" class="mb-6 rounded-2xl">
            <v-icon color="#25D366" size="60">mdi-shield-key-outline</v-icon>
          </v-avatar>
          <h3 class="text-h5 font-weight-black text-grey-darken-4 mb-2 italic">Verify Synchronization Code</h3>
          <p class="text-body-2 font-weight-bold text-grey-darken-2 mb-10 opacity-70">
            A 6-digit cryptographic token has been dispatched to your
            {{ selectedMethod === "email" ? "verified Email" : "WhatsApp registry" }}.
          </p>

          <div class="otp-premium-container mb-8">
            <v-otp-input
              v-model="otpCode"
              length="6"
              variant="outlined"
              class="premium-otp-cells"
              :disabled="isLoading"
              @finish="onOtpComplete"
            ></v-otp-input>
          </div>

          <v-expand-transition>
            <v-alert
              v-if="verificationError"
              type="error"
              variant="flat"
              class="mb-8 rounded-xl border-thin"
              density="compact"
            >
              {{ verificationError }}
            </v-alert>
          </v-expand-transition>

          <v-btn
            color="#25D366"
            size="x-large"
            block
            class="rounded-xl mb-6 text-white font-weight-black shadow-premium"
            :loading="isLoading"
            :disabled="otpCode.length < 6"
            @click="verifyOTP"
          >
            Authorize Connection
          </v-btn>

          <div class="d-flex align-center justify-center mb-4">
            <template v-if="resendTimer > 0">
              <v-chip size="small" variant="tonal" color="grey-darken-2" class="font-weight-black">
                Resend Window: {{ resendTimer }}s
              </v-chip>
            </template>
            <v-btn
              v-else
              variant="text"
              color="primary"
              class="text-capitalize font-weight-black"
              :disabled="isLoading"
              @click="requestOTP"
            >
              Regenerate Token
            </v-btn>
          </div>

          <v-btn
            variant="tonal"
            color="grey-darken-2"
            size="small"
            class="text-capitalize rounded-lg font-weight-black"
            :disabled="isLoading"
            @click="currentStep = 1"
            prepend-icon="mdi-arrow-left"
          >
            Modify Authorization Parameters
          </v-btn>
        </div>
      </v-card-text>

      <v-divider />
      <div class="pa-6 text-center bg-white-opacity-10">
        <div class="d-flex align-center justify-center opacity-60">
          <v-icon size="16" color="success" class="mr-2">mdi-shield-check</v-icon>
          <span class="text-caption font-weight-black text-grey-darken-1">Enterprise-grade communication encryption active</span>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useAppStore } from "@/stores/app";
import { doctorService } from "@/services/doctor";

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  "setup-complete": [];
}>();

const authStore = useAuthStore();
const appStore = useAppStore();

const currentStep = ref(1);
const isLoading = ref(false);
const whatsappNumber = ref("");
const selectedMethod = ref("whatsapp");
const otpCode = ref("");
const verificationError = ref("");
const isPhoneFormValid = ref(false);
const resendTimer = ref(0);
let timerInterval: any = null;

const maskedEmail = computed(() => {
  const email = authStore.doctor?.email || "";
  if (!email) return "your verified email";
  const [user, domain] = email.split("@");
  return `${user.slice(0, 3)}***@${domain}`;
});

const phoneRules = [
  (v: string) => !!v || "Operational number required",
  (v: string) =>
    /^\+?(\d[\s-]?){10,14}$/.test(v) || "Invalid global format",
];

const startTimer = () => {
  resendTimer.value = 60;
  if (timerInterval) clearInterval(timerInterval);
  timerInterval = setInterval(() => {
    if (resendTimer.value > 0) {
      resendTimer.value--;
    } else {
      clearInterval(timerInterval);
    }
  }, 1000);
};

const requestOTP = async () => {
  try {
    isLoading.value = true;
    verificationError.value = "";

    let formattedNumber = whatsappNumber.value.trim();
    if (!formattedNumber.startsWith("+")) {
      if (formattedNumber.startsWith("234"))
        formattedNumber = "+" + formattedNumber;
      else if (formattedNumber.startsWith("0"))
        formattedNumber = "+234" + formattedNumber.slice(1);
      else formattedNumber = "+234" + formattedNumber;
    }

    const response = await doctorService.sendWhatsAppOTP(
      formattedNumber,
      selectedMethod.value,
    );

    if (response.success) {
      appStore.showSuccess(
        `Authorization dispatched via ${selectedMethod.value === "email" ? "Secure Email" : "WhatsApp Direct"}`,
      );
      currentStep.value = 2;
      whatsappNumber.value = formattedNumber;
      startTimer();
    }
  } catch (error: any) {
    appStore.showError(
      "Authorization Failed",
      error.response?.data?.error || "Failed to dispatch verification payload",
    );
  } finally {
    isLoading.value = false;
  }
};

const onOtpComplete = () => {
  verifyOTP();
};

const verifyOTP = async () => {
  if (otpCode.value.length < 6) return;

  try {
    isLoading.value = true;
    verificationError.value = "";

    const response = await doctorService.verifyWhatsAppOTP(
      whatsappNumber.value,
      otpCode.value,
    );

    if (response.success) {
      appStore.showSuccess("Operational WhatsApp Verified");
      if (authStore.doctor) {
        authStore.doctor.whatsapp_verified = true;
        authStore.doctor.whatsapp_number = whatsappNumber.value;
      }
      emit("setup-complete");
      emit("update:modelValue", false);

      setTimeout(() => {
        currentStep.value = 1;
        otpCode.value = "";
        whatsappNumber.value = "";
      }, 500);
    }
  } catch (error: any) {
    verificationError.value =
      error.response?.data?.error ||
      "Invalid synchronization token. Please attempt regeneration.";
  } finally {
    isLoading.value = false;
  }
};

onUnmounted(() => {
  if (timerInterval) clearInterval(timerInterval);
});

watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen && !whatsappNumber.value) {
      whatsappNumber.value =
        authStore.doctor?.whatsapp_number || authStore.doctor?.phone || "";
    }
  },
  { immediate: true },
);
</script>

<style lang="scss" scoped>
.whatsapp-card {
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(12px) !important;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
}

.step-indicator {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f3f4f6;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 16px;
  border: 4px solid white;
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  &.active {
    background: #25d366;
    color: white;
    transform: scale(1.1);
    box-shadow: 0 10px 20px rgba(37, 211, 102, 0.2);
  }

  &.complete {
    background: #128c7e;
    color: white;
  }
}

.step-connector {
  width: 80px;
  height: 4px;
  background: #f3f4f6;
  margin: 0 12px;
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 0;
    background: #25d366;
    transition: width 0.6s ease;
  }

  &.active::after {
    width: 100%;
  }
}

.method-premium-card {
  border-radius: 20px !important;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  background: white !important;
  border: 1px solid rgba(0,0,0,0.05) !important;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 30px rgba(0,0,0,0.05) !important;
    border-color: rgba(37, 211, 102, 0.2) !important;
  }

  &.selected {
    border-color: #25d366 !important;
    background: rgba(37, 211, 102, 0.02) !important;
    box-shadow: 0 15px 40px rgba(37, 211, 102, 0.08) !important;
  }
}

.otp-premium-container {
  :deep(.v-otp-input) {
    justify-content: center;
    gap: 12px;
  }

  :deep(.v-otp-input__field) {
    width: 54px !important;
    height: 64px !important;
    border-radius: 16px !important;
    background: white !important;
    border: 2px solid rgba(0,0,0,0.05) !important;
    font-size: 28px !important;
    font-weight: 900 !important;
    color: #25d366 !important;
    transition: all 0.3s ease;

    &:focus {
      border-color: #25d366 !important;
      box-shadow: 0 8px 25px rgba(37, 211, 102, 0.15) !important;
    }
  }
}

.premium-search-field {
  :deep(.v-field) {
    border-radius: 16px !important;
    background: white !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03) !important;

    &.v-field--focused {
      box-shadow: 0 8px 30px rgba(37, 211, 102, 0.1) !important;
    }
  }
  :deep(.v-field__outline) { display: none; }
}

.shadow-premium { box-shadow: 0 20px 50px rgba(0,0,0,0.1) !important; }
.bg-white-opacity-5 { background: rgba(255, 255, 255, 0.4) !important; }
.bg-white-opacity-10 { background: rgba(255, 255, 255, 0.5) !important; }
.border-thin { border: 1px solid rgba(0,0,0,0.03) !important; }
.letter-spacing-1 { letter-spacing: 1px; }
.italic { font-style: italic; }

@keyframes slideInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-slide-in-up { animation: slideInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) both; }
</style>
